import merge from 'lodash/merge'
import { getDownloadURL, ref, uploadBytes, UploadResult } from 'firebase/storage'

import { graphQLClient } from './authService'
import { NewOfferTDO, Offer, OffersFilter, uploadImageProps } from '../types/offers'
import offerQueries from './graphql/offers/queries'
import { storage } from '../utils/firebase'
import offerMutations from './graphql/offers/mutations'
import { OfferType } from '../utils/config'

class OfferService {
  getAll = async (variables: any = {}): Promise<Offer[]> => {
    const response = await graphQLClient.request(offerQueries.getAll, variables)
    return response.offers
  }

  getOne = async (id?: string): Promise<Offer> => {
    const response = await graphQLClient.request(offerQueries.getOne, {
      where: {
        id,
      },
    })
    return response.offer
  }

  getAllWithFilter = async (
    variables: any = {},
    { cost, customers, locations, nbClaimed, search, type }: Partial<OffersFilter>
  ): Promise<Offer[]> => {
    const where: any = {
      OR: [
        { title_contains: search || '' },
        { customer: { name_contains: search || '' } },
        { places_some: { name_contains: search || '' } },
      ],
    }

    if (type && type.length === 1) {
      Object.assign(where, {
        OR: type.map((x) => ({ type: x })),
      })
    }

    if (cost) {
      where.cost_lte = cost
    }

    if (nbClaimed) {
      where.nbClaimed_lte = nbClaimed
    }

    if (locations && locations.length > 0) {
      Object.assign(where, {
        places_some: {
          id_in: locations.map((place) => place.id),
        },
      })
    }

    // params for customers
    if (customers && customers.length > 0) {
      Object.assign(where, {
        customer: {
          id_in: customers.map((customer) => customer.id),
        },
      })
    }

    const mergedVariables = merge({ where }, variables)

    return await this.getAllPaginated(mergedVariables)
  }

  uploadImage = async ({ file, customerId, offerId }: uploadImageProps): Promise<UploadResult> => {
    const timestamp = new Date().getTime()
    const fileRoute = offerId
      ? `/customers/${customerId}/offers/${offerId}/${timestamp}-${file.name}`
      : `/customers/${customerId}/offers/${timestamp}-${file.name}`
    const result = await uploadBytes(ref(storage, fileRoute), file)

    return result
  }

  uploadQr = async ({ file, customerId, offerId }: uploadImageProps): Promise<UploadResult> => {
    const timestamp = new Date().getTime()
    const fileRoute = offerId
      ? `/customers/${customerId}/offers/${offerId}/${timestamp}-${file.name}`
      : `/customers/${customerId}/offers/${timestamp}-${file.name}`
    const result = await uploadBytes(ref(storage, fileRoute), file)

    return result
  }

  createOffer = async ({
    image,
    qrCode,
    places,
    categories,
    customer,
    cost,
    activeMessage,
    ...rest
  }: Partial<Omit<NewOfferTDO, 'image' | 'qrCode'>> & {
    image: UploadResult
    qrCode: UploadResult
  }): Promise<Offer> => {
    const imageUrl = await getDownloadURL(image.ref)

    const data: any = {
      ...rest,
      places: {
        connect: places ? places.map((id) => ({ id })) : [],
      },
      customer: {
        connect: {
          id: customer?.id,
        },
      },
      categories: {
        connect: {
          id: categories,
        },
      },
      image: {
        create: {
          url: imageUrl,
          reference: image.ref.fullPath,
        },
      },
      activeMessage: activeMessage || undefined,
    }

    if (rest.type === OfferType.offer) {
      data.cost = cost
    }

    if (!rest.maxClaimable) {
      delete data.maxClaimable
    }

    if (!rest.nbPermittedClaims) {
      delete data.nbPermittedClaims
    }

    if (qrCode) {
      const qrCodeUrl = await getDownloadURL(qrCode.ref)
      data.qrCode = {
        create: {
          url: qrCodeUrl,
          reference: qrCode.ref.fullPath,
        },
      }
    }

    const response = await graphQLClient.request(offerMutations.createOffer, {
      data,
    })

    return response.createOffer
  }

  updateOffer = async ({
    image,
    qrCode,
    places,
    categories,
    customer,
    cost,
    activeMessage,
    type,
    id,
    ...rest
  }: Partial<Omit<NewOfferTDO, 'image' | 'qrCode'>> & {
    image: UploadResult
    qrCode: UploadResult
    id?: string
  }): Promise<Offer> => {
    const data: any = {
      ...rest,
      places: {
        connect: places ? places.map((id) => ({ id })) : [],
      },
      customer: {
        connect: {
          id: customer?.id,
        },
      },
      categories: {
        connect: {
          id: categories,
        },
      },
      activeMessage: activeMessage || undefined,
    }

    if (type === OfferType.offer) {
      data.cost = cost
    }

    if (image) {
      const imageUrl = await getDownloadURL(image.ref)
      data.image = {
        replace: {
          url: imageUrl,
          reference: image.ref.fullPath,
        },
      }
    }

    if (qrCode) {
      const qrCodeUrl = await getDownloadURL(qrCode.ref)
      data.qrCode = {
        replace: {
          url: qrCodeUrl,
          reference: qrCode.ref.fullPath,
        },
      }
    }

    const response = await graphQLClient.request(offerMutations.updateOffer, {
      where: {
        id,
      },
      data,
    })

    return response.updateOffer
  }

  getAllPaginated = async (variables: any = {}): Promise<Offer[]> => {
    const response = await graphQLClient.request(offerQueries.getPaginatedAll, variables)
    return response?.offersConnection?.edges?.map((offer: { node: Offer }) => offer.node) || []
  }

  deleteById = async (id: string): Promise<{ deleteOffers: number }> => {
    return await graphQLClient.request(offerMutations.deleteOffers, {
      where: {
        id_in: [id],
      },
    })
  }

  copyOffer = async (offer: Omit<Offer, 'createdAt' | 'id'>): Promise<Offer> => {
    const now = new Date()
    const { nbPermittedClaims, earnableFrom, earnableTo, redeemableFrom, redeemableTo, maxClaimable, ...restOffer } =
      offer

    const isOffer = offer.type === 'OFFER'

    const changePastDateToFuture = (date: string) => {
      if (!isOffer) {
        return date
      }

      const dateObj = new Date(date)
      if (dateObj.getTime() >= now.getTime()) {
        return date
      }

      const newDate = now.setTime(now.getTime() + 1000 * 60)
      return new Date(newDate)
    }

    const data: any = {
      ...restOffer,
      ...(nbPermittedClaims && { nbPermittedClaims: nbPermittedClaims }),
      ...(maxClaimable && { maxClaimable: maxClaimable }),
      earnableFrom,
      earnableTo: changePastDateToFuture(earnableTo),
      redeemableFrom,
      redeemableTo: changePastDateToFuture(redeemableTo),
      places: {
        connect: offer.places ? offer.places.map((place) => ({ id: place.id })) : [],
      },
      customer: {
        connect: {
          id: offer.customer?.id,
        },
      },
      categories: {
        connect: offer.categories.map((cat) => ({ id: cat.id })),
      },
      image: {
        create: offer.image,
      },
      activeMessage: offer.activeMessage || undefined,
    }

    if (offer.type === OfferType.offer) {
      data.cost = offer.cost
    }

    if (offer.qrCode) {
      data.qrCode = { create: offer.qrCode }
    }

    const response = await graphQLClient.request(offerMutations.createOffer, {
      data,
    })

    return response.createOffer
  }
}

const offerService = new OfferService()

export default offerService
